const DEFAULT_PERSONALIZATION = {
  maxNameLength: 15,
  preferredType: 'sticker',
  types: [
    {
      id: 'sticker',
      label: 'Sticker',
      priceLabel: 'FREE',
      description: 'High-quality, durable sticker with a smooth finish.',
      icon: null,
      productTags: ['sticker', 'personalise:sticker'],
      colors: [
        { id: 'white', hex: '#FFFFFF', label: 'White' },
        { id: 'black', hex: '#000000', label: 'Black' },
        { id: 'gold', hex: '#C4A871', label: 'Gold' },
        { id: 'pink', hex: '#E495AF', label: 'Pink' },
        { id: 'blue', hex: '#364C96', label: 'Blue' },
      ],
      defaultColor: 'white',
    },
    {
      id: 'engraving',
      label: 'Engraving',
      priceLabel: '+\u00A35',
      description: 'Laser-engraved for a permanent, premium finish.',
      icon: null,
      productTags: ['engraving', 'personalise:engraving'],
      colors: [
        { id: 'silver', hex: '#C0C0C0', label: 'Silver' },
        { id: 'gold', hex: '#C4A871', label: 'Gold' },
        { id: 'black', hex: '#000000', label: 'Black' },
      ],
      defaultColor: 'silver',
    },
  ],
  defaults: { type: 'sticker' },
};

if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        this.personalization = this.readPersonalizationConfig() || DEFAULT_PERSONALIZATION;

        this.modes = ['theme', 'type'];
        this.modeContainers = {};
        this.modeToggles = {};
        this.themesByMode = {};
        this.panelsByMode = {};
        this.modes.forEach((mode) => {
          const container = this.querySelector(`[data-bundle-mode-panel="${mode}"]`);
          this.modeContainers[mode] = container;
          this.modeToggles[mode] = this.querySelector(`[data-bundle-mode-toggle="${mode}"]`);
          this.themesByMode[mode] = container ? Array.from(container.querySelectorAll('[data-bundle-theme]')) : [];
          this.panelsByMode[mode] = container ? Array.from(container.querySelectorAll('[data-bundle-panel]')) : [];
        });

        const requestedMode = this.dataset.defaultMode;
        this.activeMode = this.modes.includes(requestedMode) && this.themesByMode[requestedMode].length > 0
          ? requestedMode
          : this.modes.find((m) => this.themesByMode[m].length > 0) || 'theme';

        this.fetchTemplate = this.dataset.fetchTemplate || '?view=bundle';
        this.collectionsBase = this.dataset.collectionsBase || '/collections';
        this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
        this.cartUrl = this.dataset.cartUrl || '/cart';
        this.currency = this.dataset.currency || 'USD';
        this.roundelText = (this.dataset.roundelText || '').trim();
        this.excludedProductIds = new Set(
          (this.dataset.excludeProductIds || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );

        this.cache = new Map();
        this.inflight = new Map();

        this.bundleItems = new Map();
        this.bundleItemCounter = 0;
        this.bundleSet = this.querySelector('[data-bundle-set]');
        this.bundleItemsEl = this.querySelector('[data-bundle-items]');
        this.bundleCountEls = this.querySelectorAll('[data-bundle-count]');
        this.bundleCountLabelEls = this.querySelectorAll('[data-bundle-count-label]');
        this.bundleTotalEl = this.querySelector('[data-bundle-total]');
        this.addAllBtn = this.querySelector('[data-bundle-add-all]');
        this.clearBtn = this.querySelector('[data-bundle-clear]');
        this.toggleBtn = this.querySelector('[data-bundle-toggle]');
        this.tiersEl = this.querySelector('[data-bundle-tiers]');
        this.tierEls = this.tiersEl ? Array.from(this.tiersEl.querySelectorAll('[data-bundle-tier]')) : [];
        this.tiers = this.tierEls.map((el) => ({
          qty: parseInt(el.dataset.tierQty, 10) || 0,
          discount: parseFloat(el.dataset.tierDiscount) || 0,
          title: el.dataset.tierTitle || '',
          subtitle: el.dataset.tierSubtitle || '',
        }));
        this.placeholderNoteTemplate = this.dataset.placeholderNoteTemplate || 'Add {count} more';
        this.emptyPlaceholder = {
          top: this.dataset.emptyPlaceholderTop || '',
          middle: this.dataset.emptyPlaceholderMiddle || '',
          bottom: this.dataset.emptyPlaceholderBottom || '',
        };
        this.bundleTotalOriginalEl = this.querySelector('[data-bundle-total-original]');
        this.bundleTotalFinalEl = this.querySelector('[data-bundle-total-final]');
        this.bundleSavingsEl = this.querySelector('[data-bundle-savings]');
        this.bundleSavingsPercentEl = this.querySelector('[data-bundle-savings-percent]');

        this.storageKey = `bundle-builder:${this.id}`;
        this.moneyFormatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
        });

        this.modes.forEach((mode) => {
          this.themesByMode[mode].forEach((theme) => {
            theme.addEventListener('click', () => this.selectTheme(theme.dataset.bundleTheme, mode));
          });

          const container = this.modeContainers[mode];
          if (!container) return;
          const list = container.querySelector('[data-bundle-themes]');
          const prev = container.querySelector('[data-bundle-nav="prev"]');
          const next = container.querySelector('[data-bundle-nav="next"]');
          if (prev && list) prev.addEventListener('click', () => this.scrollThemes(-1, list));
          if (next && list) next.addEventListener('click', () => this.scrollThemes(1, list));
          if (list) list.addEventListener('scroll', () => this.updateNav(mode), { passive: true });

          const toggle = this.modeToggles[mode];
          if (toggle) toggle.addEventListener('click', () => this.setMode(mode));
        });

        window.addEventListener('resize', () => {
          this.updateNav();
          this.updateBodyOffset();
        });
        this.updateNav();

        this.addEventListener('click', this.handleClick.bind(this));

        if (this.addAllBtn) this.addAllBtn.addEventListener('click', () => this.goToCheckout());
        if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clearBundle());
        if (this.toggleBtn) this.toggleBtn.addEventListener('click', () => this.toggleBundleSet());

        this.setupPersonalize();

        try { localStorage.removeItem(this.storageKey); } catch (e) { /* ignore */ }

        this.cartQueue = Promise.resolve();
        this.render();
        this.enqueue(() => this.syncFromCart());

        this.observeModeTabsVisibility();
      }

      // ---------- Personalisation config ----------

      readPersonalizationConfig() {
        const script = this.querySelector('script[data-personalize-config]');
        if (!script) return null;
        try {
          const parsed = JSON.parse(script.textContent || '{}');
          if (!parsed || !Array.isArray(parsed.types) || parsed.types.length === 0) return null;
          parsed.maxNameLength = parsed.maxNameLength || DEFAULT_PERSONALIZATION.maxNameLength;
          parsed.preferredType = parsed.preferredType || DEFAULT_PERSONALIZATION.preferredType;
          parsed.defaults = parsed.defaults || { type: parsed.types[0].id };
          parsed.upcharges = Array.isArray(parsed.upcharges) ? parsed.upcharges : [];
          return parsed;
        } catch (error) {
          console.warn('Bundle builder: invalid personalisation config JSON; using fallback.', error);
          return null;
        }
      }

      upchargeForType(typeId) {
        if (!typeId) return null;
        const upcharges = (this.personalization && this.personalization.upcharges) || [];
        return upcharges.find((u) => u && u.type === typeId && u.variantId) || null;
      }

      upchargeForItem(item) {
        if (!item || !item.personalization || !item.personalization.name) return null;
        return this.upchargeForType(item.personalization.type);
      }

      personalizationType(typeId) {
        return this.personalization.types.find((x) => x.id === typeId) || null;
      }

      personalizationTypeLabel(typeId) {
        const t = this.personalizationType(typeId);
        return t ? t.label : '';
      }

      personalizationColorsForType(typeId) {
        const t = this.personalizationType(typeId);
        return t && Array.isArray(t.colors) ? t.colors : [];
      }

      personalizationDefaultColor(typeId) {
        const t = this.personalizationType(typeId);
        if (t && t.defaultColor) return t.defaultColor;
        const colors = this.personalizationColorsForType(typeId);
        return colors.length ? colors[0].id : '';
      }

      personalizationColorLabel(typeId, colorId) {
        const c = this.personalizationColorsForType(typeId).find((x) => x.id === colorId);
        return c ? c.label : '';
      }

      // ---------- Mode switching ----------

      setMode(mode) {
        if (!this.modes.includes(mode)) return;
        if (this.activeMode === mode) return;
        this.activeMode = mode;

        this.modes.forEach((m) => {
          const container = this.modeContainers[m];
          if (container) container.dataset.active = m === mode ? 'true' : 'false';
          const toggle = this.modeToggles[m];
          if (toggle) {
            const isActive = m === mode;
            toggle.setAttribute('aria-selected', isActive ? 'true' : 'false');
            toggle.classList.toggle('bundle-builder__mode-toggle--active', isActive);
          }
        });

        const activePanel = (this.panelsByMode[mode] || []).find((p) => p.dataset.active === 'true');
        if (activePanel && activePanel.dataset.loaded !== 'true') {
          this.loadPanel(activePanel);
        }

        this.updateNav(mode);
      }

      // ---------- Theme switching + AJAX panels ----------

      selectTheme(blockId, mode) {
        if (!blockId) return;

        const resolvedMode = this.modes.includes(mode) ? mode : this.activeMode;
        if (resolvedMode !== this.activeMode) this.setMode(resolvedMode);

        const themes = this.themesByMode[resolvedMode] || [];
        const panels = this.panelsByMode[resolvedMode] || [];

        themes.forEach((theme) => {
          const isActive = theme.dataset.bundleTheme === blockId;
          theme.setAttribute('aria-selected', isActive ? 'true' : 'false');
          theme.classList.toggle('bundle-builder__theme--active', isActive);
        });

        let activePanel = null;
        panels.forEach((panel) => {
          const isActive = panel.dataset.bundlePanel === blockId;
          panel.dataset.active = isActive ? 'true' : 'false';
          if (isActive) activePanel = panel;
        });

        if (activePanel && activePanel.dataset.loaded !== 'true') {
          this.loadPanel(activePanel);
        }
      }

      loadPanel(panel) {
        const handle = panel.dataset.collectionHandle;
        if (!handle) return;

        if (this.cache.has(handle)) {
          panel.innerHTML = this.cache.get(handle);
          this.filterExcludedProducts(panel);
          panel.dataset.loaded = 'true';
          this.refreshAddButtons();
          return;
        }

        if (this.inflight.has(handle)) return;

        panel.classList.add('bundle-builder__panel--loading');
        panel.innerHTML = this.loadingMarkup();

        const base = this.collectionsBase.replace(/\/$/, '');
        const url = `${base}/${encodeURIComponent(handle)}${this.fetchTemplate}`;
        const request = fetch(url, { headers: { Accept: 'text/html' } })
          .then((response) => {
            if (!response.ok) throw new Error(`Bundle fetch failed: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const trimmed = html.trim();
            this.cache.set(handle, trimmed);
            panel.innerHTML = trimmed;
            this.filterExcludedProducts(panel);
            panel.dataset.loaded = 'true';
            this.refreshAddButtons();
          })
          .catch((error) => {
            console.error(error);
            panel.innerHTML = '<p class="bundle-builder__products-empty">Could not load products. Please try again.</p>';
          })
          .finally(() => {
            panel.classList.remove('bundle-builder__panel--loading');
            this.inflight.delete(handle);
          });

        this.inflight.set(handle, request);
      }

      filterExcludedProducts(panel) {
        if (!panel || !this.excludedProductIds || this.excludedProductIds.size === 0) return;

        let removed = 0;
        const cards = panel.querySelectorAll('[data-bundle-card-product-id]');
        cards.forEach((card) => {
          if (!this.excludedProductIds.has(card.dataset.bundleCardProductId)) return;
          const item = card.closest('.bundle-builder__product') || card;
          item.remove();
          removed += 1;
        });

        if (removed === 0) return;

        const list = panel.querySelector('.bundle-builder__products');
        if (list && list.children.length === 0) {
          list.remove();
          if (!panel.querySelector('.bundle-builder__products-empty')) {
            const empty = document.createElement('p');
            empty.className = 'bundle-builder__products-empty';
            empty.textContent = 'No products in this collection yet.';
            panel.appendChild(empty);
          }
        }
      }

      loadingMarkup() {
        return '<div class="bundle-builder__loading" role="status" aria-live="polite"><span class="bundle-builder__spinner" aria-hidden="true"></span><span class="visually-hidden">Loading products</span></div>';
      }

      scrollThemes(direction, list) {
        if (!list) return;
        const amount = list.clientWidth * 0.8 * direction;
        list.scrollBy({ left: amount, behavior: 'smooth' });
      }

      updateNav(specificMode) {
        const modes = specificMode ? [specificMode] : this.modes;
        modes.forEach((mode) => {
          const container = this.modeContainers[mode];
          if (!container) return;
          const list = container.querySelector('[data-bundle-themes]');
          const prev = container.querySelector('[data-bundle-nav="prev"]');
          const next = container.querySelector('[data-bundle-nav="next"]');
          if (!list || (!prev && !next)) return;
          const { scrollLeft, scrollWidth, clientWidth } = list;
          const atStart = scrollLeft <= 1;
          const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
          if (prev) prev.disabled = atStart;
          if (next) next.disabled = atEnd;
        });
      }

      // ---------- Bundle state ----------

      handleClick(event) {
        const personalizeBtn = event.target.closest('[data-bundle-personalize]');
        if (personalizeBtn) {
          event.preventDefault();
          this.openPersonalize(personalizeBtn);
          return;
        }

        const quantityAction = event.target.closest('[data-bundle-quantity-action]');
        if (quantityAction) {
          event.preventDefault();
          const addBtn = quantityAction.closest('[data-bundle-add]');
          if (!addBtn) return;
          if (quantityAction.dataset.bundleQuantityAction === 'decrease') {
            this.decrementProduct(addBtn.dataset.variantId);
          } else {
            this.incrementProduct(addBtn);
          }
          return;
        }

        const addMainBtn = event.target.closest('[data-bundle-add-main]');
        if (addMainBtn) {
          event.preventDefault();
          const addBtn = addMainBtn.closest('[data-bundle-add]');
          if (addBtn && this.countVariant(addBtn.dataset.variantId) === 0) this.incrementProduct(addBtn);
          return;
        }

        const removeBtn = event.target.closest('[data-bundle-remove]');
        if (removeBtn) {
          event.preventDefault();
          this.removeProduct(removeBtn.dataset.bundleRemove);
        }
      }

      toggleProduct(button) {
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        if (this.countVariant(variantId) > 0) {
          this.decrementProduct(variantId);
        } else {
          this.addProduct(this.itemFromButton(button));
        }
      }

      totalQuantity() {
        return this.bundleItems.size;
      }

      itemQuantity(item) {
        return Math.max(1, parseInt(item && item.quantity, 10) || 1);
      }

      countVariant(variantId) {
        if (!variantId) return 0;
        let count = 0;
        for (const item of this.bundleItems.values()) {
          if (String(item.variantId) === String(variantId)) count += 1;
        }
        return count;
      }

      findLastItemKeyByVariant(variantId) {
        const keys = Array.from(this.bundleItems.keys()).reverse();
        return keys.find((key) => {
          const item = this.bundleItems.get(key);
          return item && String(item.variantId) === String(variantId);
        });
      }

      findFirstItemByVariant(variantId) {
        for (const item of this.bundleItems.values()) {
          if (String(item.variantId) === String(variantId)) return item;
        }
        return null;
      }

      newBundleItemId(variantId) {
        this.bundleItemCounter += 1;
        return `${variantId}-${Date.now()}-${this.bundleItemCounter}`;
      }

      // ---------- Cart sync ----------

      enqueue(task) {
        this.cartQueue = (this.cartQueue || Promise.resolve())
          .then(() => task())
          .catch((error) => {
            console.error('Bundle/cart sync failed', error);
            this.showCartError(error);
            return this.syncFromCart().catch(() => {});
          });
        return this.cartQueue;
      }

      showCartError(error) {
        const message = error && error.message ? error.message : 'Could not update cart.';
        window.alert(message);
      }

      async fetchCart() {
        const response = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`Could not load cart (${response.status})`);
        return response.json();
      }

      async cartAdd(items) {
        if (!items || items.length === 0) return null;
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items, sections: ['cart-icon-bubble'] }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || err.message || `Failed (${response.status})`);
        }
        const json = await response.json();
        this.applyCartSections(json && json.sections);
        return json;
      }

      async cartUpdate(updates) {
        if (!updates || Object.keys(updates).length === 0) return null;
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates, sections: ['cart-icon-bubble'] }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || err.message || `Failed (${response.status})`);
        }
        const json = await response.json();
        this.applyCartSections(json && json.sections);
        return json;
      }

      applyCartSections(sections) {
        if (!sections) return;
        const bubbleHtml = sections['cart-icon-bubble'];
        if (typeof bubbleHtml !== 'string') return;
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        try {
          const parsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const next = parsed.querySelector('#cart-icon-bubble');
          if (next) bubble.innerHTML = next.innerHTML;
        } catch (e) {
          /* ignore */
        }
      }

      async syncFromCart(prefetchedCart) {
        let cart = prefetchedCart;
        if (!cart) {
          try {
            cart = await this.fetchCart();
          } catch (error) {
            console.warn('Bundle builder: could not load cart for sync', error);
            return;
          }
        }
        this.applyCartToState(cart);
        this.render();
      }

      applyCartToState(cart) {
        const newMap = new Map();
        this.bundleItemCounter = 0;
        const items = (cart && cart.items) || [];
        items.forEach((line) => {
          if (this.isLinkedUpchargeLine(line)) return;
          const quantity = Math.max(0, parseInt(line.quantity, 10) || 0);
          for (let i = 0; i < quantity; i += 1) {
            const item = this.itemFromCartLine(line);
            newMap.set(item.bundleItemId, item);
          }
        });
        this.bundleItems = newMap;
      }

      isLinkedUpchargeLine(line) {
        const props = line && line.properties;
        if (!props) return false;
        return Object.prototype.hasOwnProperty.call(props, '_linked_variant_id');
      }

      itemFromCartLine(line) {
        const props = line.properties || {};
        let personalization = null;
        const text = props.Text || props.text || '';
        if (text) {
          const typeId = this.personalizationTypeIdFromLabel(props.Type || props.type)
            || (this.personalization.defaults && this.personalization.defaults.type)
            || (this.personalization.types[0] && this.personalization.types[0].id)
            || '';
          const colorId = this.personalizationColorIdFromLabel(typeId, props.Colour || props.Color || props.color)
            || this.personalizationDefaultColor(typeId);
          personalization = { name: text, type: typeId, color: colorId };
        }

        const variantId = String(line.id);
        const imageRaw = line.image
          || (line.featured_image && line.featured_image.url)
          || '';
        const image = imageRaw ? imageRaw.replace(/(\.[a-z]+)(\?.*)?$/i, '_200x$1') : '';

        return {
          bundleItemId: this.newBundleItemId(variantId),
          lineKey: line.key || '',
          variantId,
          productId: String(line.product_id || ''),
          handle: line.handle || line.product_handle || '',
          title: line.product_title || line.title || '',
          variantTitle: line.variant_title || '',
          price: line.original_price != null ? line.original_price : (line.price || 0),
          url: line.url || (line.handle ? `/products/${line.handle}` : ''),
          image,
          supportedTypes: this.supportedTypesForVariant(variantId),
          quantity: 1,
          personalization,
        };
      }

      supportedTypesForVariant(variantId) {
        if (!variantId) return '';
        const btn = this.querySelector(
          `[data-bundle-add][data-variant-id="${CSS.escape(String(variantId))}"]`
        );
        return btn ? (btn.dataset.supportedTypes || '') : '';
      }

      personalizationTypeIdFromLabel(label) {
        if (!label) return '';
        const types = this.personalization.types || [];
        const found = types.find((t) => t.id === label || t.label === label);
        return found ? found.id : '';
      }

      personalizationColorIdFromLabel(typeId, label) {
        if (!label) return '';
        const colors = this.personalizationColorsForType(typeId);
        const found = colors.find((c) => c.id === label || c.label === label);
        return found ? found.id : '';
      }

      findCartLineForItem(cart, item) {
        if (!cart || !Array.isArray(cart.items) || !item) return null;
        const variantStr = String(item.variantId);
        const wantsPers = !!(item.personalization && item.personalization.name);
        const wantedName = wantsPers ? item.personalization.name : null;
        const wantedTypeLabel = wantsPers ? this.personalizationTypeLabel(item.personalization.type) : null;

        const candidates = cart.items.filter((line) => {
          if (this.isLinkedUpchargeLine(line)) return false;
          if (String(line.id) !== variantStr) return false;
          const props = line.properties || {};
          const lineName = props.Text || props.text || '';
          if (wantsPers) {
            if (lineName !== wantedName) return false;
            if (wantedTypeLabel) {
              const lineType = props.Type || props.type || '';
              if (lineType !== wantedTypeLabel) return false;
            }
            return true;
          }
          return !lineName;
        });
        return candidates[candidates.length - 1] || null;
      }

      findLinkedUpchargeLine(cart, variantId, personalization) {
        if (!cart || !Array.isArray(cart.items)) return null;
        const variantStr = String(variantId);
        const name = personalization && personalization.name ? personalization.name : null;
        return cart.items.find((line) => {
          const props = line.properties;
          if (!props) return false;
          if (String(props._linked_variant_id || '') !== variantStr) return false;
          if (name && (props.Text || '') !== name) return false;
          return true;
        }) || null;
      }

      itemFromButton(button) {
        const variantId = button.dataset.variantId;
        return {
          bundleItemId: this.newBundleItemId(variantId),
          variantId,
          productId: button.dataset.productId,
          handle: button.dataset.productHandle,
          title: button.dataset.productTitle,
          variantTitle: button.dataset.variantTitle,
          price: parseInt(button.dataset.productPrice, 10) || 0,
          url: button.dataset.productUrl,
          image: button.dataset.productImage,
          supportedTypes: button.dataset.supportedTypes || '',
          quantity: 1,
          personalization: null,
        };
      }

      addProduct(item) {
        if (!item || !item.variantId) return;
        if (!item.bundleItemId) item.bundleItemId = this.newBundleItemId(item.variantId);
        item.quantity = 1;
        this.bundleItems.set(item.bundleItemId, item);
        this.render();

        const variantId = item.variantId;
        this.enqueue(async () => {
          await this.cartAdd([{ id: Number(variantId), quantity: 1 }]);
        });
      }

      incrementProduct(button) {
        const variantId = button && button.dataset.variantId;
        if (!variantId) return;

        const item = this.itemFromButton(button);
        this.bundleItems.set(item.bundleItemId, item);
        this.render();

        this.enqueue(async () => {
          await this.cartAdd([{ id: Number(variantId), quantity: 1 }]);
        });
      }

      decrementProduct(variantId) {
        if (!variantId) return;
        const key = this.findLastItemKeyByVariant(variantId);
        if (!key) return;
        const item = this.bundleItems.get(key);
        if (!item) return;

        this.bundleItems.delete(key);
        this.render();

        const snapshot = { variantId: item.variantId, personalization: item.personalization || null };
        this.enqueue(async () => {
          await this.removeOneUnit(snapshot);
        });
      }

      removeProduct(bundleItemId) {
        const item = this.bundleItems.get(bundleItemId);
        if (!item) return;

        this.bundleItems.delete(bundleItemId);
        this.render();

        const snapshot = { variantId: item.variantId, personalization: item.personalization || null };
        this.enqueue(async () => {
          await this.removeOneUnit(snapshot);
        });
      }

      async removeOneUnit(item) {
        if (!item) return;
        const cart = await this.fetchCart();
        const line = this.findCartLineForItem(cart, item);
        if (!line || !line.key) return;
        const newQty = Math.max(0, (parseInt(line.quantity, 10) || 0) - 1);
        const updates = { [line.key]: newQty };
        if (newQty === 0 && item.personalization) {
          const upcharge = this.findLinkedUpchargeLine(cart, item.variantId, item.personalization);
          if (upcharge && upcharge.key) updates[upcharge.key] = 0;
        }
        await this.cartUpdate(updates);
      }

      clearBundle() {
        if (this.bundleItems.size === 0) return;

        this.bundleItems.clear();
        this.render();

        this.enqueue(async () => {
          const cart = await this.fetchCart();
          const updates = {};
          (cart.items || []).forEach((line) => {
            if (line && line.key) updates[line.key] = 0;
          });
          if (Object.keys(updates).length > 0) await this.cartUpdate(updates);
        });
      }

      // ---------- Personalization ----------

      setupPersonalize() {
        const sectionScopedId = this.id.replace(/^BundleBuilder-/, '');
        this.modal = document.getElementById(`PersonalizeModal-${sectionScopedId}`);
        if (!this.modal) return;

        this.modalForm = this.modal.querySelector('[data-personalize-form]');
        this.modalTitle = this.modal.querySelector('[data-personalize-title]');
        this.modalSubtitle = this.modal.querySelector('[data-personalize-subtitle]');
        this.modalImage = this.modal.querySelector('[data-personalize-image]');
        this.modalPreview = this.modal.querySelector('[data-personalize-preview]');
        this.modalNameInput = this.modal.querySelector('[data-personalize-name]');
        this.modalNameHint = this.modal.querySelector('[data-personalize-name-hint]');
        this.modalTypesEl = this.modal.querySelector('[data-personalize-types]');
        this.modalColorsEl = this.modal.querySelector('[data-personalize-colors]');
        this.modalCancelBtn = this.modal.querySelector('[data-personalize-cancel]');

        if (this.modalNameInput) {
          this.modalNameInput.maxLength = this.personalization.maxNameLength;
        }
        if (this.modalNameHint) {
          this.modalNameHint.textContent = `Maximum ${this.personalization.maxNameLength} characters`;
        }

        if (this.modalForm) {
          this.modalForm.addEventListener('submit', (event) => {
            event.preventDefault();
            this.savePersonalize();
          });
          this.modalForm.addEventListener('input', () => this.updatePersonalizePreview());
          this.modalForm.addEventListener('change', (event) => {
            this.updatePersonalizePreview();
            if (event.target && event.target.name === 'type') {
              const currentColorInput = this.modalForm.querySelector('input[name="color"]:checked');
              const currentColor = currentColorInput ? currentColorInput.value : '';
              this.renderColorsForType(event.target.value, currentColor);
            }
          });
        }
        if (this.modalCancelBtn) {
          this.modalCancelBtn.addEventListener('click', () => this.modal.hide());
        }
      }

      renderPersonalizeOptions(types) {
        if (!this.modalTypesEl) return;

        const list = types && types.length ? types : this.personalization.types;

        const legend = this.modalTypesEl.querySelector('legend');
        this.modalTypesEl.innerHTML = '';
        if (legend) this.modalTypesEl.appendChild(legend);

        const wrapperList = document.createElement('div');
        wrapperList.className = 'bundle-personalize__type-list';
        list.forEach((type) => {
          const labelId = `${this.id}-type-${type.id}`;
          const wrapper = document.createElement('label');
          wrapper.className = 'bundle-personalize__type';
          wrapper.htmlFor = labelId;
          wrapper.innerHTML = `
            <input
              id="${this.escape(labelId)}"
              type="radio"
              name="type"
              value="${this.escape(type.id)}"
              class="bundle-personalize__type-input visually-hidden"
            >
            <span class="bundle-personalize__type-radio" aria-hidden="true"></span>
            <span class="bundle-personalize__type-icon" aria-hidden="true">
              ${this.renderTypeIconMarkup(type)}
            </span>
            <span class="bundle-personalize__type-content">
              <span class="bundle-personalize__type-heading">
                <span class="bundle-personalize__type-title">${this.escape(type.label)}</span>
                <span class="bundle-personalize__type-price">(${this.escape(type.priceLabel)})</span>
              </span>
              <span class="bundle-personalize__type-description">${this.escape(type.description)}</span>
            </span>
          `;
          wrapperList.appendChild(wrapper);
        });
        this.modalTypesEl.appendChild(wrapperList);
      }

      renderTypeIconMarkup(type) {
        if (type.icon) {
          return `<img src="${this.escape(type.icon)}" alt="" loading="lazy" width="32" height="32">`;
        }
        const path = type.id === 'engraving'
          ? '<path d="M6 3v8c0 3 2.5 5 6 5s6-2 6-5V3M9 21h6M12 16v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>'
          : '<path d="M12 2l2.39 6.96H22l-6.18 4.49L18.18 22 12 17.27 5.82 22l2.36-8.55L2 8.96h7.61L12 2z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>';
        return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">${path}</svg>`;
      }

      renderColorsForType(typeId, preselectedColorId) {
        if (!this.modalColorsEl) return;

        const legend = this.modalColorsEl.querySelector('legend');
        this.modalColorsEl.innerHTML = '';
        if (legend) this.modalColorsEl.appendChild(legend);

        const colors = this.personalizationColorsForType(typeId);
        if (colors.length === 0) {
          this.modalColorsEl.hidden = true;
          return;
        }
        this.modalColorsEl.hidden = false;

        const hasPreselected = !!(preselectedColorId && colors.find((c) => c.id === preselectedColorId));
        const toCheck = hasPreselected ? preselectedColorId : this.personalizationDefaultColor(typeId);

        const list = document.createElement('div');
        list.className = 'bundle-personalize__color-list';
        colors.forEach((color) => {
          const labelId = `${this.id}-color-${color.id}`;
          const wrapper = document.createElement('label');
          wrapper.className = 'bundle-personalize__color';
          wrapper.htmlFor = labelId;
          wrapper.style.setProperty('--swatch-color', color.hex);
          const checkedAttr = color.id === toCheck ? ' checked' : '';
          wrapper.innerHTML = `
            <input
              id="${this.escape(labelId)}"
              type="radio"
              name="color"
              value="${this.escape(color.id)}"
              class="bundle-personalize__color-input visually-hidden"${checkedAttr}
            >
            <span class="bundle-personalize__color-swatch" aria-hidden="true"></span>
            <span class="visually-hidden">${this.escape(color.label)}</span>
          `;
          list.appendChild(wrapper);
        });
        this.modalColorsEl.appendChild(list);
      }

      openPersonalize(button) {
        if (!this.modal) return;

        const variantId = button.dataset.variantId;
        if (!variantId) return;
        this.activeVariantId = variantId;
        this.activeBundleItemId = button.dataset.bundleItemId || '';

        let item = this.activeBundleItemId
          ? this.bundleItems.get(this.activeBundleItemId)
          : this.findFirstItemByVariant(variantId);
        if (!item) {
          item = this.itemFromButton(button);
        }
        this.activeItemDraft = item;

        const availableTypes = this.availableTypesForButton(button, item);
        if (availableTypes.length === 0) {
          return;
        }
        this.renderPersonalizeOptions(availableTypes);

        const title = button.dataset.productTitle || item.title || '';
        const variantTitle = button.dataset.variantTitle || item.variantTitle || '';
        const image = button.dataset.productImage || item.image || '';

        if (this.modalTitle) this.modalTitle.textContent = title;
        if (this.modalSubtitle) {
          const showVariant = variantTitle && variantTitle !== 'Default Title';
          this.modalSubtitle.textContent = showVariant ? variantTitle : '';
          this.modalSubtitle.hidden = !showVariant;
        }
        if (this.modalImage) {
          this.modalImage.src = image;
          this.modalImage.alt = title;
        }

        const fallbackTypeId = availableTypes[0].id;
        const saved = item.personalization;
        const savedTypeValid = saved && availableTypes.some((t) => t.id === saved.type);
        const personalization = saved && savedTypeValid
          ? saved
          : {
              name: saved ? saved.name : '',
              type: fallbackTypeId,
              color: this.personalizationDefaultColor(fallbackTypeId),
            };

        if (this.modalNameInput) this.modalNameInput.value = personalization.name || '';
        const typeInput = this.modalForm.querySelector(`input[name="type"][value="${CSS.escape(personalization.type)}"]`);
        if (typeInput) typeInput.checked = true;

        this.renderColorsForType(personalization.type, personalization.color);

        this.updatePersonalizePreview();
        this.modal.show(button);
      }

      availableTypesForButton(button, item) {
        const fromButton = (button && button.dataset.supportedTypes) || '';
        const fromItem = (item && item.supportedTypes) || '';
        const raw = fromButton || fromItem || '';
        const ids = raw.split(',').map((s) => s.trim()).filter(Boolean);

        let result;
        if (ids.length === 0) {
          return [];
        }
        result = this.personalization.types.filter((t) => ids.includes(t.id));
        if (result.length === 0) {
          return [];
        }
        result = result.slice();

        const preferredId = this.personalization.preferredType;
        if (preferredId) {
          const preferredIdx = result.findIndex((t) => t.id === preferredId);
          if (preferredIdx > 0) {
            const [preferred] = result.splice(preferredIdx, 1);
            result.unshift(preferred);
          }
        }

        return result;
      }

      updatePersonalizePreview() {
        if (!this.modalPreview || !this.modalNameInput) return;
        this.modalPreview.textContent = this.modalNameInput.value;
        this.modalNameInput.setCustomValidity('');
      }

      savePersonalize() {
        if (!this.modal || !this.activeVariantId) return;

        const formData = new FormData(this.modalForm);
        const name = (formData.get('name') || '').toString().trim().slice(0, this.personalization.maxNameLength);
        const type = (formData.get('type') || this.personalization.defaults.type).toString();
        const color = (formData.get('color') || this.personalizationDefaultColor(type)).toString();

        if (!name) {
          if (this.modalNameInput) {
            this.modalNameInput.setCustomValidity('Please enter a name to personalise this item.');
            this.modalNameInput.reportValidity();
            this.modalNameInput.focus();
          }
          return;
        }

        const targetItem = this.activeBundleItemId
          ? this.bundleItems.get(this.activeBundleItemId)
          : this.findFirstItemByVariant(this.activeVariantId);
        const draft = this.activeItemDraft || null;

        const variantId = (targetItem && targetItem.variantId)
          || (draft && draft.variantId)
          || this.activeVariantId;
        if (!variantId) {
          this.modal.hide();
          return;
        }

        const existing = targetItem && targetItem.personalization;
        const noChange = existing
          && existing.name === name
          && existing.type === type
          && existing.color === color;
        if (noChange) {
          this.modal.hide();
          return;
        }

        const productTitle = (targetItem && targetItem.title) || (draft && draft.title) || '';
        const typeLabel = this.personalizationTypeLabel(type);
        const colorLabel = this.personalizationColorLabel(type, color);
        const upcharge = this.upchargeForType(type);

        const oldPersonalization = existing ? { ...existing } : null;

        if (targetItem) {
          targetItem.personalization = { name, type, color };
        } else if (draft) {
          if (!draft.bundleItemId) draft.bundleItemId = this.newBundleItemId(draft.variantId);
          draft.personalization = { name, type, color };
          this.bundleItems.set(draft.bundleItemId, draft);
        }
        this.render();
        this.modal.hide();

        this.enqueue(async () => {
          const cart = await this.fetchCart();
          const oldLine = this.findCartLineForItem(cart, { variantId, personalization: oldPersonalization });
          if (oldLine && oldLine.key) {
            const updates = {};
            updates[oldLine.key] = Math.max(0, (parseInt(oldLine.quantity, 10) || 0) - 1);
            if (updates[oldLine.key] === 0 && oldPersonalization) {
              const oldUpcharge = this.findLinkedUpchargeLine(cart, variantId, oldPersonalization);
              if (oldUpcharge && oldUpcharge.key) updates[oldUpcharge.key] = 0;
            }
            await this.cartUpdate(updates);
          }

          const properties = {
            Text: name,
            Type: typeLabel,
            Colour: colorLabel,
          };
          const toAdd = [{ id: Number(variantId), quantity: 1, properties }];
          if (upcharge && upcharge.variantId) {
            toAdd.push({
              id: Number(upcharge.variantId),
              quantity: 1,
              properties: {
                'Personalisation for': productTitle,
                Text: name,
                Type: typeLabel,
                Colour: colorLabel,
                _linked_variant_id: String(variantId),
              },
            });
          }
          await this.cartAdd(toAdd);
        });
      }

      // ---------- Cart ----------

      goToCheckout() {
        if (this.bundleItems.size === 0) return;
        window.location.href = '/checkout';
      }

      // ---------- Rendering ----------

      render() {
        this.renderItems();
        this.renderTotal();
        this.renderTiers();
        this.renderVisibility();
        this.refreshAddButtons();
      }

      renderTiers() {
        if (!this.tierEls || this.tierEls.length === 0) return;

        const count = this.totalQuantity();

        let activeIndex = -1;
        this.tierEls.forEach((tier, index) => {
          const qty = parseInt(tier.dataset.tierQty, 10) || 0;
          if (qty > 0 && count >= qty) activeIndex = index;
        });

        this.tierEls.forEach((tier, index) => {
          const reached = index <= activeIndex;
          const active = index === activeIndex;
          tier.dataset.tierReached = reached ? 'true' : 'false';
          tier.dataset.tierActive = active ? 'true' : 'false';
        });
      }

      renderItems() {
        if (!this.bundleItemsEl) return;
        this.bundleItemsEl.innerHTML = '';
        for (const item of this.bundleItems.values()) {
          this.bundleItemsEl.prepend(this.createItemNode(item));
        }

        const currentCount = this.totalQuantity();

        if (currentCount === 0 && this.hasEmptyPlaceholder()) {
          this.bundleItemsEl.appendChild(this.createEmptyPlaceholderNode());
        }

        const sortedTiers = (this.tiers || []).slice().sort((a, b) => a.qty - b.qty);
        const unreachedTiers = sortedTiers.filter((t) => t.qty > currentCount);
        const maxSlots = Math.max(0, 4 - currentCount);
        const tiersToShow = unreachedTiers.slice(0, maxSlots);
        const lastTierQty = sortedTiers.length > 0 ? sortedTiers[sortedTiers.length - 1].qty : 0;

        tiersToShow.forEach((tier) => {
          const needed = Math.max(0, tier.qty - currentCount);
          const isLastTier = tier.qty === lastTierQty;
          const countStr = isLastTier ? `${needed}+` : `${needed}`;
          const noteText = this.placeholderNoteTemplate.replace('{count}', countStr);
          this.bundleItemsEl.appendChild(this.createPlaceholderNode(tier, noteText));
        });
      }

      hasEmptyPlaceholder() {
        const ep = this.emptyPlaceholder;
        return !!(ep && (ep.top || ep.middle || ep.bottom));
      }

      createEmptyPlaceholderNode() {
        const li = document.createElement('li');
        li.className = 'bundle-set-item bundle-set-item--placeholder bundle-set-item--placeholder--empty';
        li.setAttribute('aria-hidden', 'true');
        li.dataset.bundlePlaceholder = '';
        const { top, middle, bottom } = this.emptyPlaceholder;
        li.innerHTML = `
          <div class="bundle-set-item__placeholder-card">
            ${top ? `<span class="bundle-set-item__placeholder-title">${this.escape(top)}</span>` : ''}
            ${middle ? `<span class="bundle-set-item__placeholder-discount">${this.escape(middle)}</span>` : ''}
            ${bottom ? `<span class="bundle-set-item__placeholder-note">${this.escape(bottom)}</span>` : ''}
          </div>
        `;
        return li;
      }

      createPlaceholderNode(tier, noteText) {
        const li = document.createElement('li');
        li.className = 'bundle-set-item bundle-set-item--placeholder';
        li.setAttribute('aria-hidden', 'true');
        li.dataset.bundlePlaceholder = '';
        const title = tier && tier.title ? tier.title : '';
        const subtitle = tier && tier.subtitle ? tier.subtitle : '';
        li.innerHTML = `
          <div class="bundle-set-item__placeholder-card">
            ${title ? `<span class="bundle-set-item__placeholder-title">${this.escape(title)}</span>` : ''}
            ${subtitle ? `<span class="bundle-set-item__placeholder-discount">${this.escape(subtitle)}</span>` : ''}
            ${noteText ? `<span class="bundle-set-item__placeholder-note">${this.escape(noteText)}</span>` : ''}
          </div>
        `;
        return li;
      }

      createItemNode(item) {
        const li = document.createElement('li');
        li.className = 'bundle-set-item';
        li.dataset.bundleItemId = item.bundleItemId;
        li.dataset.variantId = item.variantId;

        const showVariant = item.variantTitle && item.variantTitle !== 'Default Title';
        const personalized = !!(item.personalization && item.personalization.name);
        const personalizeLabel = personalized ? 'Edit personalisation' : '+ Add Personalisation';
        const canPersonalize = !!(item.supportedTypes && item.supportedTypes.length > 0);
        const personalizeHiddenClass = canPersonalize ? '' : ' bundle-set-item__personalize--unavailable';
        const personalizeA11yAttrs = canPersonalize ? '' : ' aria-hidden="true" tabindex="-1"';

        const upcharge = this.upchargeForItem(item);
        const upchargeLabel = upcharge
          ? `+ ${this.escape(this.personalizationTypeLabel(item.personalization.type) || upcharge.productTitle || 'Personalisation')} (${this.formatMoney(Number(upcharge.price) || 0)})`
          : '';

        const personalizeButton = `<button
              type="button"
              class="bundle-set-item__personalize${personalized ? ' bundle-set-item__personalize--set' : ''}${personalizeHiddenClass}"
              data-bundle-personalize
              data-bundle-item-id="${this.escape(item.bundleItemId)}"
              data-variant-id="${this.escape(item.variantId)}"
              data-product-id="${this.escape(item.productId)}"
              data-product-handle="${this.escape(item.handle)}"
              data-product-title="${this.escape(item.title)}"
              data-variant-title="${this.escape(item.variantTitle)}"
              data-product-price="${this.escape(item.price)}"
              data-product-url="${this.escape(item.url)}"
              data-product-image="${this.escape(item.image)}"
              data-supported-types="${this.escape(item.supportedTypes)}"
              ${personalizeA11yAttrs}
            >${this.escape(personalizeLabel)}</button>`;

        li.innerHTML = `
          <div class="bundle-set-item__media">
            ${item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.title)}" loading="lazy">` : ''}
          </div>
          <div class="bundle-set-item__info">
            <a class="bundle-set-item__title" href="${this.escape(item.url)}">${this.escape(item.title)}</a>
            ${showVariant ? `<div class="bundle-set-item__meta">${this.escape(item.variantTitle)}</div>` : ''}
            <div class="bundle-set-item__price-row">
              <div class="bundle-set-item__price">${this.formatMoney(item.price)}</div>
              ${upchargeLabel ? `<div class="bundle-set-item__upcharge">${upchargeLabel}</div>` : ''}
            </div>
            ${personalizeButton}
          </div>
          <button type="button" class="bundle-set-item__remove" data-bundle-remove="${this.escape(item.bundleItemId)}" aria-label="Remove ${this.escape(item.title)}">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M3 3l8 8M11 3L3 11" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </button>
        `;
        return li;
      }

      renderTotal() {
        let productSubtotal = 0;
        let upchargeSubtotal = 0;
        for (const item of this.bundleItems.values()) {
          const quantity = this.itemQuantity(item);
          productSubtotal += (item.price || 0) * quantity;
          const upcharge = this.upchargeForItem(item);
          if (upcharge) upchargeSubtotal += (Number(upcharge.price) || 0) * quantity;
        }
        const discountPercent = this.getActiveDiscount();
        const discountedProducts = discountPercent > 0
          ? Math.round((productSubtotal * (100 - discountPercent)) / 100)
          : productSubtotal;
        const subtotal = productSubtotal + upchargeSubtotal;
        const discounted = discountedProducts + upchargeSubtotal;
        const showStrike = discountPercent > 0 && productSubtotal > 0 && discounted < subtotal;

        if (this.bundleTotalOriginalEl) {
          this.bundleTotalOriginalEl.textContent = this.formatMoney(subtotal);
          this.bundleTotalOriginalEl.hidden = !showStrike;
        }
        if (this.bundleTotalFinalEl) {
          this.bundleTotalFinalEl.textContent = this.formatMoney(discounted);
        }
        if (this.bundleTotalEl && !this.bundleTotalFinalEl) {
          this.bundleTotalEl.textContent = this.formatMoney(discounted);
        }

        if (this.bundleSavingsEl) {
          const showSavings = showStrike;
          if (showSavings && this.bundleSavingsPercentEl) {
            this.bundleSavingsPercentEl.textContent = String(Math.round(discountPercent));
          }
          this.bundleSavingsEl.hidden = !showSavings;
        }

        const count = this.totalQuantity();

        if (this.bundleCountEls) {
          this.bundleCountEls.forEach((el) => {
            el.textContent = String(count);
          });
        }
        if (this.bundleCountLabelEls) {
          const label = count === 1 ? 'item' : 'items';
          this.bundleCountLabelEls.forEach((el) => {
            el.textContent = label;
          });
        }

        if (this.addAllBtn) {
          this.addAllBtn.disabled = count === 0;
        }
      }

      getActiveDiscount() {
        if (!this.tiers || this.tiers.length === 0) return 0;
        const count = this.totalQuantity();
        let discount = 0;
        this.tiers.forEach((tier) => {
          if (tier.qty > 0 && count >= tier.qty) discount = tier.discount;
        });
        return discount;
      }

      renderVisibility() {
        if (!this.bundleSet) return;
        this.bundleSet.hidden = false;
        const empty = this.bundleItems.size === 0;
        this.bundleSet.classList.toggle('bundle-set--collapsed', empty);
        if (!empty) {
          this.bundleSet.classList.add('bundle-set--visible');
          if (this.modeTabsObserver) {
            this.modeTabsObserver.disconnect();
            this.modeTabsObserver = null;
          }
        }
        if (this.toggleBtn) {
          this.toggleBtn.setAttribute('aria-expanded', empty ? 'false' : 'true');
        }
        this.updateBodyOffset();
      }

      observeModeTabsVisibility() {
        if (!this.bundleSet) return;
        const reveal = (collapsed) => {
          this.bundleSet.classList.toggle('bundle-set--collapsed', collapsed);
          this.bundleSet.classList.add('bundle-set--visible');
          if (this.toggleBtn) {
            this.toggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
          }
          this.updateBodyOffset();
        };
        if (this.bundleItems.size > 0) {
          reveal(false);
          return;
        }
        const modeTabs = this.querySelector('[data-bundle-mode-tabs]');
        if (!modeTabs || typeof IntersectionObserver === 'undefined') {
          reveal(true);
          return;
        }
        this.modeTabsObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              if (!scrolledPast) return;
              reveal(true);
              if (this.modeTabsObserver) {
                this.modeTabsObserver.disconnect();
                this.modeTabsObserver = null;
              }
            });
          },
          { threshold: 0 }
        );
        this.modeTabsObserver.observe(modeTabs);
      }

      disconnectedCallback() {
        if (this.modeTabsObserver) {
          this.modeTabsObserver.disconnect();
          this.modeTabsObserver = null;
        }
      }

      updateBodyOffset() {
        if (!this.bundleSet) return;
        const onScreen = !this.bundleSet.hidden && this.bundleSet.classList.contains('bundle-set--visible');
        if (!onScreen) {
          document.body.style.removeProperty('padding-bottom');
          return;
        }
        const apply = () => {
          const height = this.bundleSet.getBoundingClientRect().height;
          if (height > 0) document.body.style.paddingBottom = `${height}px`;
        };
        apply();
        requestAnimationFrame(apply);
      }

      refreshAddButtons() {
        const addButtons = this.querySelectorAll('[data-bundle-add]');
        addButtons.forEach((btn) => {
          const quantity = this.countVariant(btn.dataset.variantId);
          const inBundle = quantity > 0;
          btn.classList.toggle('bundle-card__add--added', inBundle);
          btn.dataset.quantity = String(quantity);
          btn.setAttribute('aria-label', inBundle ? `${quantity} added to bundle` : 'Add to bundle');

          const mainBtn = btn.querySelector('[data-bundle-add-main]');
          if (mainBtn) mainBtn.setAttribute('aria-pressed', inBundle ? 'true' : 'false');

          const labelEl = btn.querySelector('[data-bundle-add-label]');
          if (labelEl) {
            const itemLabel = quantity === 1 ? 'item' : 'items';
            labelEl.textContent = inBundle ? `${quantity} ${itemLabel} added` : 'Add to Bundle';
          }
        });

        const personalizeButtons = this.querySelectorAll('[data-bundle-personalize]');
        personalizeButtons.forEach((btn) => {
          const hasTypes = !!(btn.dataset.supportedTypes || '').trim();

          if (btn.classList.contains('bundle-card__personalize')) {
            btn.classList.toggle('bundle-card__personalize--unavailable', !hasTypes);
            if (hasTypes) {
              btn.removeAttribute('aria-hidden');
              btn.removeAttribute('tabindex');
            } else {
              btn.setAttribute('aria-hidden', 'true');
              btn.setAttribute('tabindex', '-1');
            }

            const item = this.findFirstItemByVariant(btn.dataset.variantId);
            const personalized = !!(item && item.personalization && item.personalization.name);
            btn.classList.toggle('bundle-card__personalize--set', personalized);
            const labelEl = btn.querySelector('[data-personalize-label]');
            if (labelEl) labelEl.textContent = personalized ? 'Personalised' : 'Personalise';
          } else if (btn.classList.contains('bundle-set-item__personalize')) {
            btn.classList.toggle('bundle-set-item__personalize--unavailable', !hasTypes);
            if (hasTypes) {
              btn.removeAttribute('aria-hidden');
              btn.removeAttribute('tabindex');
            } else {
              btn.setAttribute('aria-hidden', 'true');
              btn.setAttribute('tabindex', '-1');
            }

            const item = btn.dataset.bundleItemId
              ? this.bundleItems.get(btn.dataset.bundleItemId)
              : this.findFirstItemByVariant(btn.dataset.variantId);
            const personalized = !!(item && item.personalization && item.personalization.name);
            btn.classList.toggle('bundle-set-item__personalize--set', personalized);
            btn.textContent = personalized ? 'Edit personalisation' : '+ Personalise';
          }
        });

        this.applyRoundels();
      }

      applyRoundels() {
        const roundels = this.querySelectorAll('[data-bundle-roundel]');
        roundels.forEach((el) => {
          el.textContent = this.roundelText || '';
          const card = el.closest('.bundle-card');
          if (!card) return;
          const target = card.querySelector('.card__inner') || card.querySelector('.card-wrapper');
          if (target && el.parentNode !== target) {
            target.appendChild(el);
          }
        });
      }

      toggleBundleSet() {
        if (!this.bundleSet || !this.toggleBtn) return;
        const collapsed = this.bundleSet.classList.toggle('bundle-set--collapsed');
        this.toggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        this.updateBodyOffset();
      }

      // ---------- Utils ----------

      formatMoney(cents) {
        if (typeof cents !== 'number' || Number.isNaN(cents)) cents = 0;
        return this.moneyFormatter.format(cents / 100);
      }

      escape(value) {
        if (value === undefined || value === null) return '';
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }
    }
  );
}
